import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('hicall-theme');
    return saved || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('hicall-theme', theme);
    // Update document class for Tailwind dark mode
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const isDark = theme === 'dark';

  // Theme colors
  const colors = {
    dark: {
      bg: '#0a0a0f',
      bgCard: 'from-gray-900 to-gray-950',
      bgCardSolid: '#111118',
      border: 'border-gray-800',
      borderColor: '#1f2937',
      text: 'text-white',
      textSecondary: 'text-gray-400',
      textMuted: 'text-gray-500',
      input: 'bg-gray-800 border-gray-700 text-white placeholder-gray-500',
      navBg: 'bg-[#0a0a0f]/80',
      navActive: 'bg-[#3272F1]/20 text-[#3272F1]',
      navInactive: 'text-gray-400 hover:text-white hover:bg-gray-800',
    },
    light: {
      bg: '#f8fafc',
      bgCard: 'from-white to-gray-50',
      bgCardSolid: '#ffffff',
      border: 'border-gray-200',
      borderColor: '#e5e7eb',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      textMuted: 'text-gray-500',
      input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
      navBg: 'bg-white/80',
      navActive: 'bg-[#3272F1]/10 text-[#3272F1]',
      navInactive: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
    }
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark,
    colors: colors[theme]
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
